import { useEffect, useRef } from 'react';
import { appVersionLabel } from './version';
import { Link, NavLink, Navigate, Route, Routes, useLocation, useParams, useSearchParams } from 'react-router-dom';
import type { Me } from './api';
import { DetailModal } from './components/DetailModal';
import { ErrorBoundary } from './components/ErrorBoundary';
import { DETAIL_PARAM } from './components/detail-link';
import { FollowProvider } from './components/follow';
import { SourceCatalogProvider } from './components/sources';
import { ArchivePage } from './pages/Archive';
import { BriefingByIdPage, BriefingPage } from './pages/Briefing';
import { CrossFitPage } from './pages/CrossFit';
import { EventsPage } from './pages/Events';
import { FollowsPage } from './pages/Follows';
import { HowItWorksPage } from './pages/HowItWorks';
import { TrainingHistoryPage } from './pages/TrainingHistory';
import { QualityCasePage, QualityListPage } from './pages/Quality';
import { SubjectPage } from './pages/Subject';
import { SubjectsPage } from './pages/Subjects';
import { SearchPage } from './pages/Search';
import { StatusPage } from './pages/Status';
import { useTheme } from './theme';
import { formatLocalDay, localToday } from './format';

const NAV = [
  { to: '/', label: 'Briefing', end: true },
  { to: '/suche', label: 'Suche' },
  { to: '/crossfit', label: 'CrossFit' },
  { to: '/agenda', label: 'Agenda' },
  { to: '/verfolgt', label: 'Verfolgt' },
  { to: '/subjekte', label: 'Subjekte' },
  { to: '/ausgaben', label: 'Ausgaben' },
  { to: '/datenstatus', label: 'Datenstatus' },
];

/** Old card links (e.g. from notifications) open the same details over the briefing. */
function LegacyCardRedirect() {
  const { versionId = '' } = useParams();
  return <Navigate to={`/?${DETAIL_PARAM}=${encodeURIComponent(versionId)}`} replace />;
}

function LegacyBriefingRedirect() {
  const { id = '' } = useParams();
  return <Navigate to={`/ausgaben/${encodeURIComponent(id)}`} replace />;
}

export function Shell({ me, onLogout }: { me: Me | null; onLogout: () => void }) {
  const { theme, deliberate, toggle, followSystem } = useTheme();
  const [params] = useSearchParams();
  const location = useLocation();
  const detail = params.get(DETAIL_PARAM);
  const main = useRef<HTMLElement>(null);
  const firstPath = useRef(location.pathname);

  // A page change moves focus to the content and scrolls to the top, as a page load would.
  useEffect(() => {
    if (location.pathname === firstPath.current) return;
    firstPath.current = location.pathname;
    if (!/jsdom/i.test(navigator.userAgent)) window.scrollTo?.(0, 0);
    main.current?.focus({ preventScroll: true });
  }, [location.pathname]);

  return (
    <FollowProvider>
      <SourceCatalogProvider>
      <div id="app-shell">
        <a className="skip" href="#content">
          Zum Inhalt
        </a>
        <header className="masthead">
          <div className="masthead-inner">
            <Link className="brand" to="/" aria-label="Hugarheim, zum Briefing">
              <img src="/brand/forest-amber-96.png" alt="" width="48" height="48" />
              <span className="brand-name">
                Hugarheim<small>Dein Tag. Deine Themen.</small>
              </span>
            </Link>
            <nav aria-label="Hauptnavigation" className="main-nav">
              <ul>
                {NAV.map((n) => (
                  <li key={n.to}>
                    <NavLink to={n.to} end={n.end}>
                      {n.label}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </nav>
          </div>
        </header>

        <main className="main" id="content" tabIndex={-1} ref={main}>
          <div className="topbar">
            <p>
              {formatLocalDay(localToday())}
              <br />
              Europe/Zurich
            </p>
            <div className="top-actions">
              <button
                type="button"
                className="small-button"
                onClick={toggle}
                aria-label={theme === 'dark' ? 'Zum hellen Farbschema wechseln' : 'Zum dunklen Farbschema wechseln'}
              >
                {theme === 'dark' ? 'Hell' : 'Dunkel'}
              </button>
              {deliberate && (
                <button type="button" className="small-button" onClick={followSystem} title="Farbschema wieder dem System überlassen">
                  System
                </button>
              )}
              <button type="button" className="small-button" onClick={onLogout} title={me?.username ? `Angemeldet als ${me.username}` : undefined}>
                Abmelden
              </button>
            </div>
          </div>

          <ErrorBoundary resetKey={location.pathname}>
          <Routes>
            <Route path="/" element={<BriefingPage />} />
            <Route path="/suche" element={<SearchPage />} />
            <Route path="/crossfit" element={<CrossFitPage />} />
            <Route path="/crossfit/verlauf" element={<TrainingHistoryPage />} />
            <Route path="/agenda" element={<EventsPage />} />
            <Route path="/events" element={<Navigate to={`/agenda${location.search}`} replace />} />
            <Route path="/verfolgt" element={<FollowsPage />} />
            <Route path="/subjekte" element={<SubjectsPage />} />
            <Route path="/subjekte/:id" element={<SubjectPage />} />
            <Route path="/qualitaet" element={<QualityListPage />} />
            <Route path="/qualitaet/:id" element={<QualityCasePage />} />
            <Route path="/ausgaben" element={<ArchivePage />} />
            <Route path="/ausgaben/:id" element={<BriefingByIdPage />} />
            <Route path="/datenstatus" element={<StatusPage />} />
            <Route path="/so-funktionierts" element={<HowItWorksPage />} />
            {/* Earlier paths keep working. */}
            <Route path="/cards/version/:versionId" element={<LegacyCardRedirect />} />
            <Route path="/briefings/:id" element={<LegacyBriefingRedirect />} />
            <Route path="/history" element={<Navigate to="/ausgaben" replace />} />
            <Route path="/search" element={<Navigate to={`/suche${location.search}`} replace />} />
            <Route path="/follows" element={<Navigate to="/verfolgt" replace />} />
            <Route path="/status" element={<Navigate to="/datenstatus" replace />} />
            <Route
              path="*"
              element={
                <div className="state state-empty">
                  <h1>Nicht gefunden</h1>
                  <p>
                    <Link to="/">Zur aktuellen Ausgabe</Link>
                  </p>
                </div>
              }
            />
          </Routes>
          </ErrorBoundary>

          <footer className="footer">
            <span>
              Hugarheim · persönlich, nachvollziehbar, ruhig. · <Link to="/so-funktionierts">So funktioniert Hugarheim</Link> · <Link to="/qualitaet">Qualität</Link>
            </span>
            <span>
              Forest Amber · <span className="app-version" title="Ausgelieferte Version">{appVersionLabel}</span>
            </span>
          </footer>
        </main>
      </div>
      {detail && (
        <ErrorBoundary resetKey={detail}>
          <DetailModal key="detail" versionId={detail} />
        </ErrorBoundary>
      )}
      </SourceCatalogProvider>
    </FollowProvider>
  );
}
